import { Client, ClientList, ClientSummary, PixelDomain } from '@/types/client';
import fetchWithAuth from './base';
import { Mailbox } from '@/types/user';
import { ListResponse } from '@/types/common';
import { IntegrationFieldMappingResponse } from '@/types/integration';

import JSONBig from 'json-bigint';

export const getClients = async (
  startingAfter?: string,
  status?: string,
  search?: string,
  agencyId?: string,
  limit?: number,
  planId?: string
): Promise<ClientList> => {
  const params = new URLSearchParams();
  if (startingAfter) {
    params.append('starting_after', startingAfter);
  }
  if (status && status !== 'all') {
    params.append('status', status);
  }
  if (search) {
    params.append('search', search);
  }
  if (agencyId) {
    params.append('agency_id', agencyId);
  }
  if (limit) {
    params.append('limit', limit.toString());
  }
  if (planId && planId !== 'all') {
    params.append('plan_id', planId);
  }

  const response = await fetchWithAuth(`/api/client?${params.toString()}`, {
    method: 'GET',
  });
  const text = await response.text();
  return JSONBig({ storeAsString: true }).parse(text);
};

export const getClientCount = async (
  search?: string,
  agencyId?: string
): Promise<{
  total: number;
  onboarding?: number;
  live?: number;
  not_started?: number;
  [key: string]: number | undefined;
}> => {
  const params = new URLSearchParams();
  if (search) {
    params.append('search', search);
  }
  if (agencyId) {
    params.append('agency_id', agencyId);
  }

  const response = await fetchWithAuth(`/api/client/count?${params.toString()}`, {
    method: 'GET',
  });
  return response.json();
};

export const getClient = async (id: string): Promise<Client> => {
  const response = await fetchWithAuth(`/api/client/${id}`, {
    method: 'GET',
  });
  const text = await response.text();
  return JSONBig({ storeAsString: true }).parse(text);
};

export const getClientSummary = async (): Promise<ListResponse<ClientSummary>> => {
  const response = await fetchWithAuth('/api/client_summary', {
    method: 'GET',
  });
  return response.json();
};

export const updateClientSummary = async (
  id: string,
  data: Partial<ClientSummary>
): Promise<ClientSummary> => {
  const response = await fetchWithAuth(`/api/client_summary/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  return response.json();
};

// Workspace PATCH bodies are not workspace representations; callers refresh with GET.
export const updateClient = async (id: string, data: Partial<Client>): Promise<void> => {
  const payload = { ...data };
  delete payload.visitor_app_id;
  await fetchWithAuth(`/api/client/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
};

export const getClientMailBoxList = async (
  id: string,
  search?: string
): Promise<ListResponse<Mailbox>> => {
  const params = new URLSearchParams();
  params.append('mailbox_status', 'connected');
  if (search) {
    params.append('search', search);
  }

  const response = await fetchWithAuth(`/api/client/${id}/mailbox?${params.toString()}`, {
    method: 'GET',
  });
  return response.json();
};

export const createWorkspace = async (data: {
  name: string;
  website: string;
  primary_contact?: {
    name: string;
    phone?: string;
    email: string;
  };
  billing_contact?: {
    name: string;
    phone?: string;
    email: string;
  };
  superagents?: Array<{ name: string; status: string; start_date: string }>;
}): Promise<Client> => {
  const response = await fetchWithAuth('/api/workspace', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (response.status === 204) {
    return null as any;
  }
  return response.json();
};

export const updateWorkspace = async (
  data: { status: string },
  clientId: string
): Promise<void> => {
  await fetchWithAuth(`/api/workspace/${clientId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
};

export const uploadClientLogo = async (clientId: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await fetchWithAuth(`/api/client/${clientId}/logo`, {
    method: 'POST',
    body: formData,
  });
  return response.json();
};

export const getClientIntegrations = async (clientId: string) => {
  const response = await fetchWithAuth(`/api/client/${clientId}/integrations`, {
    method: 'GET',
  });
  return response.json();
};

export const getIntegrationFieldMapping = async (
  clientId: string,
  integrationType: string,
  integrationWorkflowId: string
): Promise<IntegrationFieldMappingResponse> => {
  const response = await fetchWithAuth(
    `/api/client/${clientId}/${integrationType}/integration_workflow/${integrationWorkflowId}/field_mapping`,
    { method: 'GET' }
  );
  return response.json();
};

export const addPixelDomain = async (clientId: string, domain: string): Promise<PixelDomain> => {
  const response = await fetchWithAuth(`/api/client/${clientId}/pixel_domain`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ domain }),
  });
  return response.json();
};

export const patchDomainExcludePages = async (
  clientId: string,
  domainId: string,
  excludePages: string[]
): Promise<PixelDomain> => {
  const response = await fetchWithAuth(`/api/client/${clientId}/pixel_domain/${domainId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ exclude_pages: excludePages }),
  });
  return response.json();
};

export const patchDomainFormTrackUrls = async (
  clientId: string,
  domainId: string,
  formTrackUrls: string[]
): Promise<PixelDomain> => {
  const response = await fetchWithAuth(`/api/client/${clientId}/pixel_domain/${domainId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ form_track_urls: formTrackUrls }),
  });
  return response.json();
};

export const deletePixelDomain = async (clientId: string, domainId: string): Promise<void> => {
  await fetchWithAuth(`/api/client/${clientId}/pixel_domain/${domainId}`, {
    method: 'DELETE',
  });
};
